import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type {
  ProcessorSummaryResult,
  ProgramSummaryResult,
  PrincipalSummaryResult,
} from "../../components/Billing/types";

const post = (endpoint: string, body: object) =>
  axios.request({
    baseURL: `${window.env.API_URL}/v1/programs/billing-mis`,
    url: endpoint,
    method: "POST",
    timeout: 30000,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      "Content-Type": "application/json",
    },
    data: body,
  });

export const fetchProcessorSummary = createAsyncThunk(
  "billingMIS/fetchProcessorSummary",
  async (billingMonth: string, { rejectWithValue }) => {
    try {
      const res = await post("/summary", { billingMonth });
      return res.data as ProcessorSummaryResult;
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || "Failed to load processor summary");
    }
  }
);

export const fetchProgramSummary = createAsyncThunk(
  "billingMIS/fetchProgramSummary",
  async (billingMonth: string, { rejectWithValue }) => {
    try {
      const res = await post("/program-summary", { billingMonth });
      return res.data as ProgramSummaryResult;
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || "Failed to load program summary");
    }
  }
);

export const fetchPrincipalSummary = createAsyncThunk(
  "billingMIS/fetchPrincipalSummary",
  async (billingMonth: string, { rejectWithValue }) => {
    try {
      const res = await post("/principal-summary", { billingMonth });
      return res.data as PrincipalSummaryResult;
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || "Failed to load principal summary");
    }
  }
);

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface BillingMISState {
  processorSummary:  ProcessorSummaryResult  | null;
  programSummary:    ProgramSummaryResult    | null;
  principalSummary:  PrincipalSummaryResult  | null;
  loading:           boolean;
  error:             string | null;
}

const initialState: BillingMISState = {
  processorSummary:  null,
  programSummary:    null,
  principalSummary:  null,
  loading:           false,
  error:             null,
};

const billingMISSlice = createSlice({
  name: "billingMIS",
  initialState,
  reducers: {
    clearSummary: (state) => {
      state.processorSummary = null;
      state.programSummary   = null;
      state.principalSummary = null;
      state.error            = null;
    },
  },
  extraReducers: (builder) => {
    const pending    = (state: BillingMISState) => { state.loading = true;  state.error = null; };
    const rejected   = (state: BillingMISState, action: any) => { state.loading = false; state.error = action.payload as string; };

    builder
      .addCase(fetchProcessorSummary.pending,   pending)
      .addCase(fetchProcessorSummary.fulfilled, (state, action) => { state.loading = false; state.processorSummary = action.payload; })
      .addCase(fetchProcessorSummary.rejected,  rejected)

      .addCase(fetchProgramSummary.pending,   pending)
      .addCase(fetchProgramSummary.fulfilled, (state, action) => { state.loading = false; state.programSummary = action.payload; })
      .addCase(fetchProgramSummary.rejected,  rejected)

      .addCase(fetchPrincipalSummary.pending,   pending)
      .addCase(fetchPrincipalSummary.fulfilled, (state, action) => { state.loading = false; state.principalSummary = action.payload; })
      .addCase(fetchPrincipalSummary.rejected,  rejected);
  },
});

export const { clearSummary } = billingMISSlice.actions;
export default billingMISSlice.reducer;
